import * as app from 'durandal/app';

export class FlexSelectDataService {
  public queryPromiseCallback: (searchQuery: string, flexselect: any) => JQueryPromise<any>;

  public querySecondaryPromiseCallback: (searchQuery: string, flexselect: any) => Promise<any>;

  private secondaryCachePromises: Array<Promise<Array<any>>> = [];

  // All flexselects that are using this service right now will be in here
  public flexselects: Array<any> = [];

  public selectCallback: (paymentToUpdate: any, newValue: any, flexSelect: any) => void;

  public flexSelectTextUpdatedCallback: (searchQuery: string, flexSelect: any) => void;

  private queuedSearchQuery: string = null;

  private searchStatus = 'loaded';

  public appEventsClearCache: Array<string> = []; // Clear cached items when any of these events happen in the app

  private cachePromises: Array<JQueryPromise<Array<any>>> = [];

  private appEventsClearCacheEvents: Array<DurandalEventSubscription> = [];

  public cacheEnabled = true;

  /**
   * How long to wait between searches
   */
  public bounceTimeout = 50;

  /**
   * If set then items will be reduced through this function (Can this to remove duplicates for example)
   */
  public itemsReducerFunction: any;

  public attached() {
    // Clear cache on realtime events
    $.each(this.appEventsClearCache, (index, appEventClearCache) => {
      const clearCacheEvent = app.on(appEventClearCache).then(() => {
        this.clearCache();
      });
      this.appEventsClearCacheEvents.push(clearCacheEvent);
    });

    let searchItemsPrimary: Array<any> = [];
    let searchItemsSecondary: Array<any> = [];

    let bounceTimeout: number | null = null;
    let secondaryBounceTimeout: number | null = null;

    const flexSelectTextUpdatedCallback = (searchQuery: string, flexSelect: any) => {
      searchQuery = $.trim(searchQuery);
      this.queuedSearchQuery = searchQuery;

      const deferred = $.Deferred();

      const updateFlexselectItems = () => {
        const getItems = () => {
          const items = [...searchItemsPrimary, ...searchItemsSecondary];

          if (this.itemsReducerFunction) {
            return items.reduce(this.itemsReducerFunction, []);
          }

          return items;
        };

        flexSelect.updateItems(getItems());
      };

      // Primary search
      if (bounceTimeout) {
        clearTimeout(bounceTimeout);
      }

      const trimmedSearchQuery = $.trim(searchQuery);

      // If result is already cached we don't need to add a bounce timeout
      const isCached = this.cacheEnabled && !!this.cachePromises[trimmedSearchQuery];
      const searchBounceTimeout = isCached ? 0 : this.bounceTimeout;

      bounceTimeout = window.setTimeout(() => {
        this.searchStatus = 'loading';
        this.queryDataFromPromse(searchQuery, flexSelect).then((dataResults: any) => {
          // The results of the query match what was searched last
          if (searchQuery == this.queuedSearchQuery) {
            searchItemsPrimary = dataResults;

            updateFlexselectItems();
            deferred.resolve(dataResults);

            this.searchStatus = 'loaded';
          }
        });
      }, searchBounceTimeout);

      // Allows for additional secondary query (This is a seprate query)
      if (secondaryBounceTimeout) {
        clearTimeout(secondaryBounceTimeout);
      }

      // If result is already cached we don't need to add a bounce timeout
      const isSecondarySearchCached =
        this.cacheEnabled && !!this.secondaryCachePromises[trimmedSearchQuery];
      const secondarySearchBounceTimeout = isSecondarySearchCached ? 0 : this.bounceTimeout;

      secondaryBounceTimeout = window.setTimeout(() => {
        if (this.querySecondaryPromiseCallback) {
          const getQueryPromise = () => {
            if (this.cacheEnabled && !!this.secondaryCachePromises[trimmedSearchQuery]) {
              return this.secondaryCachePromises[trimmedSearchQuery];
            }

            const queryPromise = this.querySecondaryPromiseCallback(trimmedSearchQuery, flexSelect);

            if (this.cacheEnabled) {
              this.secondaryCachePromises[trimmedSearchQuery] = queryPromise;
            }

            return queryPromise;
          };

          getQueryPromise().then((dataResults: any) => {
            searchItemsSecondary = dataResults;

            updateFlexselectItems();
          });
        }
      }, secondarySearchBounceTimeout);

      return deferred.promise();
    };

    this.flexSelectTextUpdatedCallback = flexSelectTextUpdatedCallback.bind(this);
  }

  public queryDataFromPromse(searchQuery: string, flexSelect: any): JQueryPromise<Array<any>> {
    searchQuery = $.trim(searchQuery);
    if (!this.cacheEnabled) {
      return this.queryPromiseCallback(searchQuery, flexSelect);
    }

    if (!this.cachePromises[searchQuery]) {
      this.cachePromises[searchQuery] = this.queryPromiseCallback(searchQuery, flexSelect).fail(
        () => {
          this.cachePromises[searchQuery] = null;
        }
      );
    }

    return this.cachePromises[searchQuery];
  }

  public detached() {
    $.each(this.appEventsClearCacheEvents, (index, appEventsClearCacheEvent) => {
      appEventsClearCacheEvent.off();
    });

    this.clearCache();
  }

  public clearCache() {
    this.cachePromises = [];

    this.secondaryCachePromises = [];

    $.each(this.flexselects, (index, flexselect) => {
      flexselect.updateItems([]);
    });
  }

  public addFlexSelect(flexSelect: any) {
    this.flexselects.push(flexSelect);
  }

  public removeFlexSelect(flexSelectToRemove: any) {
    this.flexselects = $.grep(this.flexselects, (value) => value != flexSelectToRemove);
  }

  // We can clear labels of any flexselect by the value that is bound to it. All items in this array are visible on frontend
  public clearFlexSelectByBoundValue(boundToValue) {
    $.each(this.flexselects, (index, flexselect) => {
      if (flexselect.settings.value == boundToValue) {
        flexselect.setInitialValue(null);
      }
    });
  }

  // Query data set with "" and if there is just 1 item in the array we'll force the value of itemToUpdate to that
  public setQueryEmptySingle(itemToUpdate: KnockoutObservable<any>) {
    return this.queryDataFromPromse('', null).then((results) => {
      if (results.length == 1) {
        itemToUpdate(results[0]);
      }
    });
  }
}
